/*
 * @Description: 神策曝光埋点指令， 当需要曝光的元素相对于浏览器视口展示 80% 的时候会触发埋点上报
 * @FilePath: \directive\exposure.ts
 * @Example v-exposure="callback"
 * @Example v-exposure="{ callback:callback, once:true }"
 */

import type { DirectiveBinding } from 'vue';

/**
 * 曝光指令配置选项
 */
interface IExposureOptions {
  /** 曝光回调函数 */
  callback?: (el: HTMLElement) => void;
  /** 曝光阈值，默认 0.8 */
  threshold?: number;
  /** 根边距，默认 '0px' */
  rootMargin?: string;
  /** 是否只曝光一次，默认 true */
  once?: boolean;
  /** 绑定曝光事件的时间戳，用于被 keep-alive 的页面回退后需要重新触发曝光的逻辑 */
  ts?: number;
  /** 曝光延迟时间，单位毫秒，默认 500 */
  stay_duration?: number;
}

/**
 * 曝光指令的 binding value 类型
 * 可以是函数或配置对象
 */
type ExposureValue = ((el: HTMLElement) => void) | IExposureOptions;

/**
 * 类型守卫：判断是否为函数类型
 */
function isExposureFunction(value: ExposureValue): value is (el: HTMLElement) => void {
  return typeof value === 'function';
}

/**
 * 扩展 HTMLElement 类型，添加自定义属性
 */
interface IExposureElement extends HTMLElement {
  _exposureTimer?: {
    timeoutId?: ReturnType<typeof setTimeout> | null;
  } | null;
  _exposureObserver?: IntersectionObserver | null;
}

/**
 * 处理曝光逻辑的通用函数
 */
const handleExposure = (el: IExposureElement, binding: DirectiveBinding<ExposureValue>): void => {
  try {
    const value = binding.value;

    // 判断 value 是函数还是配置对象
    const isFunction = isExposureFunction(value);
    const options = isFunction ? undefined : value;

    const threshold = options?.threshold ?? 0.8; // 曝光阈值
    const rootMargin = options?.rootMargin ?? '0px';
    const callback = isFunction ? value : options?.callback;
    const once = isFunction || options?.once === undefined ? true : options.once;
    const ts = options?.ts; // 绑定曝光事件的时间戳
    const stayDuration = options?.stay_duration ?? 500; // 曝光延迟时间，单位毫秒

    if (!callback) {
      console.warn('exposure directive: callback is required');
      return;
    }

    // 存储计时器ID
    if (!el._exposureTimer) {
      el._exposureTimer = {};
    }

    const observerOptions: {
      root: Element | null;
      rootMargin: string;
      threshold: number;
    } = {
      root: document.getElementById('app'),
      rootMargin,
      threshold,
    };

    const observer = new IntersectionObserver((entries) => {
      if (entries.length === 0) return;

      const entry = entries[0];

      // 如果元素已经曝光过，且只需要曝光一次，则不再处理
      if (
        once &&
        (el.getAttribute('is-exposured') || (ts && `${ts}` !== el.getAttribute('bind-time')))
      ) {
        observer.unobserve(el);
        return;
      }

      if (entry.isIntersecting) {
        // 元素进入可视区域，设置延迟计时器
        if (el._exposureTimer?.timeoutId) {
          clearTimeout(el._exposureTimer.timeoutId);
        }

        if (!el._exposureTimer) {
          el._exposureTimer = {};
        }
        el._exposureTimer.timeoutId = setTimeout(() => {
          // 再次检查元素是否仍然在可视区域内
          if (entry.isIntersecting) {
            callback(el);
            if (once) {
              observer.unobserve(el);
              el.setAttribute('is-exposured', 'true');
            }
          }
        }, stayDuration);
      } else {
        // 元素离开可视区域，清除计时器并更新bind-time
        if (el._exposureTimer?.timeoutId) {
          clearTimeout(el._exposureTimer.timeoutId);
          el._exposureTimer.timeoutId = null;
        }

        // 更新bind-time的值
        if (ts) {
          el.setAttribute('bind-time', String(ts));
        }
      }
    }, observerOptions);

    el.removeAttribute('is-exposured');
    setTimeout(() => {
      observer.observe(el);
    }, 200);

    if (ts) {
      el.setAttribute('bind-time', String(ts));
    }

    // 清理旧的observer
    if (el._exposureObserver) {
      el._exposureObserver.disconnect();
    }

    // 存储新的observer引用
    el._exposureObserver = observer;
  } catch (err) {
    console.log(err);
  }
};

export const exposure = {
  // 只调用一次，指令第一次绑定到元素时调用。在这里可以进行一次性的初始化设置。
  mounted(el: IExposureElement, binding: DirectiveBinding<ExposureValue>): void {
    handleExposure(el, binding);
  },

  updated(el: IExposureElement, binding: DirectiveBinding<ExposureValue>): void {
    const value = binding.value;
    const isFunction = isExposureFunction(value);
    const options = isFunction ? undefined : value;
    const ts = options?.ts; // 绑定曝光事件的时间戳
    const bindTime = el.getAttribute('bind-time')
      ? parseFloat(el.getAttribute('bind-time') || '0')
      : '';
    if (!ts || !bindTime || ts === bindTime) return;

    handleExposure(el, binding);
  },

  // 当指令与元素解绑时调用
  unmounted(el: IExposureElement): void {
    // 清除计时器
    if (el._exposureTimer?.timeoutId) {
      clearTimeout(el._exposureTimer.timeoutId);
      el._exposureTimer = null;
    }

    // 断开observer连接
    if (el._exposureObserver) {
      el._exposureObserver.disconnect();
      el._exposureObserver = null;
    }
  },
};

export default {
  exposure,
};
